
import AppPublicLink from "@/components/PublicLink.vue";
import {Contact} from "@/models/base";
import {ExtraFieldNamesConfig} from "@/models/dossier-configs/extra-field-names-config";
import {Sale} from "@/models/base";
import {SaleRow} from "@/models/base";
import {DossierConfigService} from "@/services/dossier-config-service";
import {SaleService} from "@/services/sale-service";
import {IImportSalesModalState, SaleEditState} from "@/utils/sale-edit/sale-edit-state";
import Vue from "vue";
import {ConfirmResult, ISelectItem} from "winfakt-vue-components";
import AppExtraFieldsEdit from "../ExtraFieldsEdit.vue";
import AppImportSale from "./SaleEditImportSale.vue";
import AppImportSaleRow from "./SaleEditImportSaleRow.vue";
import AppContactInfoModal from "@/components/contacts/ContactInfoModal.vue";
import AppApplyDiscount from "@/components/sales/ApplyDiscount.vue";
import {UserConfigService} from "@/services/user-config-service";
import {BrowserInfo} from "@/utils/browser";
import {TranslateResult} from "vue-i18n";
import {CustomerService } from "@/services";
import {IClientStats} from "@/utils/client-stats";
import {AuthService} from "@/services/auth-service";
import {OpenClientInfoModalAfterSelectOptions } from "@/models/user-configs/sale-settings";
import {UserInfo} from "@/models/dossier/user-info";
import { SaleEditStateButtonsList } from "@/utils/sale-edit/sale-edit-state-buttons-list";

interface DiscountReturn {
	discount: number,
	full: boolean
};

export default Vue.extend({
	filters: {
		trim(string: string) {
			return string.trim();
		}
	},
	data() {
		return {
			SaleSettings: UserConfigService.getSaleSettings(),
			clientStats: {} as IClientStats,
			connectedUsers: null as null | UserInfo[],
			userId: AuthService.wfUser.ID,
			activeImportSaleTab: 0,
			hoverCardStyle: {top: "0px", left: "0px"} as {top:string, left:string},
			showHoverCard: false,
			hideHoverCardTimer: null as null | ReturnType<typeof setTimeout>
		};
	},
	props: {
		state: {
			required: true,
			type: Object as () => SaleEditState
		},
	},

	computed: {
		showLoyaltyBalance():boolean{
			return this.state.loyalty.shouldShowButton();
		},
		moreText():string {
			if (!this.sale.Contact || !this.sale.Contact.MainAddress) return "";
			if (!this.sale.Contact.MainAddress.AddressLine1) return "";
			if (this.sale.Contact.MainAddress.PostalAndCity) {
				return `${this.sale.Contact.MainAddress.AddressLine1}, ${this.sale.Contact.MainAddress.PostalAndCity}` + " | ";
			}
			return `${this.sale.Contact.MainAddress.AddressLine1}` + " | ";
		},
		hoverAddressLines(): string[] {
			let address = this.sale.Contact && this.sale.Contact.MainAddress;
			if (!address) return [];
			let lines:string[] = [];
			if (address.AddressLine1) lines.push(address.AddressLine1);
			if (address.AddressLine2) lines.push(address.AddressLine2);
			let postalCity = address.PostalAndCity || [address.Postal, address.City].filter(v=>v).join(" ");
			if (postalCity) lines.push(postalCity);
			if (address.CountryName && address.CountryCode != "BE") lines.push(address.CountryName);
			return lines;
		},
		hoverEmail(): string {
			if (!this.sale.Contact || !this.sale.Contact.MainAddress) return "";
			return this.sale.Contact.MainAddress.getDefaultEmail();
		},
		importSalesModalState():IImportSalesModalState {
			return this.state.importSalesModal;
		},
		connectedUsersList():string{
			if(this.connectedUsers == null){
				return "";
			}
			return this.connectedUsers.map((user:UserInfo) => user.Firstname || user.Email).join(", ");
		},
		contactInfoModal: {
			get():boolean {
				return this.state.contactInfoModal != OpenClientInfoModalAfterSelectOptions.OFF;
			},
			set(value:boolean){
				if (value) {
					this.state.contactInfoModal = OpenClientInfoModalAfterSelectOptions.INFO;
				}else{
					this.state.contactInfoModal = OpenClientInfoModalAfterSelectOptions.OFF;
				}
			}
		},
		isMobile(): boolean {
			return BrowserInfo.isMobile();
		},
		sale(): Sale {
			return this.state.sale;
		},
		displayExtraFields(): any {
			let d = DossierConfigService.getExtraFieldNamesConfigs().getContactNamesByGroupID(1);
			return d ? true : false;
		},
		documentImportText(): TranslateResult {
			if (this.state.isPurchaseType) {
				return this.$ct("purchase-document-import");
			}
			return this.$ct("document-import");
		},
		buttonList():SaleEditStateButtonsList{
			return new SaleEditStateButtonsList(this.state);
		},
		quickButtonItems():ISelectItem<Function>[] {
			let items = this.buttonList.generateButtons(this.SaleSettings.SaleEditQuickButtons);
			return items;
		},
		buttonBarItems(): ISelectItem<Function>[] {
			let items = this.buttonList.generateButtons(this.SaleSettings.SaleEditHamburgerButtons);
			if (!this.$wf.isMdUp) {
				let favouriteButtons = this.quickButtonItems;
				items.unshift(...favouriteButtons);
				items = items.filter((a,i)=>{
					let foundI = items.findIndex(a2=>a2.key == a.key);
					return foundI == i;
				});
			}
			return items;
		},
		extraFieldsNameSettings(): ExtraFieldNamesConfig {
			return DossierConfigService.getExtraFieldNamesConfigs().SaleNames;
		},
		saleTypeName(): string {
			return this.sale.getTypeName(this.$i18n.locale);
		},
		importSaleTabs(): TranslateResult[] {
			let tabs = Object.values(this.$ct("ImportSaleTabs"));
			return tabs;
		},
	},
	watch: {
		async "state.sale.ContactID"(){
			if(this.state.sale.ContactID){
				this.clientStats = await CustomerService.getClientStats(this.sale.ContactID);
			}
		},
		"state.importSalesModal.contactGroupId"(){
			this.activeImportSaleTab = this.state.importSalesModal.contactGroupId-1;
		},
		"activeImportSaleTab"(){
			this.state.importSalesModal.contactGroupId = this.activeImportSaleTab + 1;
		}
	},
	methods: {
		trigger(value: string) {
			(this as any)[value]();
		},
		onContactEnter(e: MouseEvent) {
			let el = e.currentTarget as HTMLElement;
			if (el) {
				let box = el.getBoundingClientRect();
				this.hoverCardStyle = {top: `${box.bottom}px`, left: `${box.left}px`};
			}
			this.clearHideHoverCard();
			this.showHoverCard = true;
		},
		clearHideHoverCard() {
			if (this.hideHoverCardTimer) {
				clearTimeout(this.hideHoverCardTimer);
				this.hideHoverCardTimer = null;
			}
		},
		scheduleHideHoverCard() {
			this.clearHideHoverCard();
			this.hideHoverCardTimer = setTimeout(() => {
				this.showHoverCard = false;
			}, 150);
		},
		openLoyaltyRedeemModal(){
			if (!this.state.loyalty.isRedeemAllowed()) return;
			this.state.loyalty.modalOpen = true;
		},
		recover() {
			this.state.restoreRecovery();
		},
		async save() {
			if (!this.sale.ContactID) {
				this.$wf.notify(this.$ct("error-contact-empty").toString(), "danger", 3500, "exclamation-triangle");
				return false;
			}
			if (this.sale.Rows.length <= 0) {
				this.$wf.notify(this.$ct("error-sale-empty").toString(), "danger", 3500, "exclamation-triangle");
				return false;
			}
			//Ticket? > save sale, open ticketrefund
			this.state.openSaveModal();
		},
		clearContact() {
			this.sale.ContactID = 0;
			this.sale.CompanyName = "";
			this.sale.Contact = new Contact();
			this.sale.VatRegime = 1;
		},
		async importSaleCheckVatRegime(sale:Sale):Promise<ConfirmResult>{
			if (sale.VatRegime == this.state.sale.VatRegime){
				return new Promise((resolve)=>resolve(ConfirmResult.NO));
			}
			let vatRegimeName =  this.$ct(`common.vat-regimes.${sale.VatRegime}`);
			let currentSaleVatRegimeName = this.$ct(`common.vat-regimes.${this.state.sale.VatRegime}`);
			let text = this.$ct("warning-imported-vat-regime-is-not-equal-to-this-vat-regime", {
				saleNr: sale.ComputedFriendlyID,
				editSaleVatRegime: currentSaleVatRegimeName,
				newVatRegime: vatRegimeName,
			});
			let confirm = this.$confirmBuilder().setWarningTitle().setMessage(text).setCancelText(this.$ct("common.dont-ask-again"));
			return confirm.show();
		},
		getImportTargetRow(): SaleRow | null {
			if (!DossierConfigService.getSaleConfigs().ImportSaleAtSelectedRow) return null;
			let row = this.state.selectedRow;
			if (!row || row.ProductSku || !this.sale.Rows.includes(row)) return null;
			return row;
		},
		replaceImportTargetRow(targetRow: SaleRow | null, rows: SaleRow[]) {
			if (!targetRow || rows.length == 0) return;
			let index = this.sale.Rows.indexOf(targetRow);
			this.sale.Rows.splice(index, 1, ...rows);
			this.sale.Rows.forEach((row: SaleRow, i: number) => row.DisplayOrder = i);
			this.state.selectedRow = new SaleRow();
		},
		async importSaleRows(items: SaleRow[]) {
			let config = DossierConfigService.getSaleConfig(this.sale.TypeID);
			let targetRow = this.getImportTargetRow();
			let importedRows: SaleRow[] = [];
			items.forEach(row => {
				if (config.ReduceImportedSaleBackorder) {
					if (row.Backorder <= 0) {
						return;
					}
					row.setAmount(row.Backorder);
				}
				row.ImportedSaleRowID = row.ID;
				row.ImportedSaleID = row.SaleID;
				row.ID = 0;
				row.SaleID = this.sale.ID;
				row.Delivered = 0;
				if (targetRow) {
					importedRows.push(row);
				} else {
					this.sale.Rows.push(row);
				}
			});
			this.replaceImportTargetRow(targetRow, importedRows);

			this.state.importSalesModal.saleRowsShown = false;
			this.state.sale.calculateTotals();
		},
		// eslint-disable-next-line max-lines-per-function
		async importSales(items: Sale[]) {
			let dontAskVatRegimeChange = false;
			let c = await DossierConfigService.getSaleConfig(this.sale.TypeID);
			let addedRowsFromOtherClientGroup:SaleRow[] = [];
			let targetRow = this.getImportTargetRow();
			let importedRows: SaleRow[] = [];
			let addRow = (row: SaleRow) => {
				if (targetRow) {
					importedRows.push(row);
				} else {
					this.sale.Rows.push(row);
				}
			};
			for (let i in items) {
				let sale = await SaleService.getSale(items[i].ID, items[i].BookYear);
				let isFromOtherClientGroup = false;
				if (sale.Type && this.sale.Type){
					if (sale.Type.ContactGroupID == 2 && this.sale.Type.ContactGroupID != 2) {
						isFromOtherClientGroup = true;
					}
					if (this.sale.Type.ContactGroupID == 2 && this.sale.Type.ContactGroupID != 2) {
						isFromOtherClientGroup = true;
					}
				}
				if (!dontAskVatRegimeChange){
					let result = await this.importSaleCheckVatRegime(sale);
					if (result === ConfirmResult.CANCEL) {
						dontAskVatRegimeChange = true;
					}else if (result === ConfirmResult.YES){
						this.state.onVatRegimeChange(sale.VatRegime);
					}
				}
				items[i] = sale;
				let row = new SaleRow();
				row.ProductDescription = await SaleService.getSaleImportRowDescription(sale.ID, this.sale.getLng() || "nl-be");
				row.ImportedSaleID = sale.ID;
				row.setAmount(0.0);
				row.Duration = 0;
				row.UnitPrice = 0.0;
				row.UnitBuyPrice = 0.0;
				if(c.HideSaleImportReferenceRows){
					row.getRowConfig().Hidden=true;
				}
				addRow(row);
				sale.Rows.forEach(row => {
					if (c.ReduceImportedSaleBackorder) {
						if (row.Backorder <= 0) {
							return;
						}
						row.setAmount(row.Backorder);
					}
					row.ImportedSaleRowID = row.ID;
					row.ImportedSaleID = sale.ID;
					row.ID = 0;
					row.SaleID = this.sale.ID;
					row.Delivered = 0;
					addRow(row);
					if (isFromOtherClientGroup) {
						addedRowsFromOtherClientGroup.push(row);
					}
				});
				this.sale.DeliveryAddressID = sale.DeliveryAddressID;
				if (sale.ExtraFields && !c.PreventCopyOfEvWhileImporting){
					this.sale.ExtraFields = sale.ExtraFields;
					this.sale.ExtraFields.ID = this.sale.ExtraFields.ID;
				}
				if (this.SaleSettings.AddEmptyLineAfterImport) {
					addRow(SaleRow.newEmptyRow(this.SaleSettings.DefaultQtyOnEmptyLine));
				}
			}
			this.replaceImportTargetRow(targetRow, importedRows);
			this.importSalesModalState.shown = false;
			this.state.sale.calculateTotals();
			if (addedRowsFromOtherClientGroup.length != 0) {
				let message = this.$ct("would-you-like-to-update-prices-to-current-prices");
				let confirm = await this.$confirmBuilder().setMessage(message).setTitle(this.$ct("common.info")).show();
				if (confirm) {
					await this.state.updateAllRowsToLatestPrices(addedRowsFromOtherClientGroup);
				}
			}
		},
		applySalesDiscount(e: DiscountReturn) {
			this.state.applySaleDiscount(e.discount, e.full);
			this.state.applyDiscountModal = false;
		},
		onListUpdated(){
			this.connectedUsers = this.state.openedClientsWatcher.getConnectedUsers().filter((u:UserInfo) => u.ID != this.userId );
		}
	},
	async mounted() {
		let c = DossierConfigService.getSaleConfig(this.sale.TypeID);
		if (this.sale.ID == 0 && c.OpenExtraFields) {
			this.state.editExtraFieldsModal = true;
		}
		if(this.state.sale.ContactID){
			this.clientStats = await CustomerService.getClientStats(this.sale.ContactID);
		}
	},
	created(){
		this.state.openedClientsWatcher.on("list-update", this.onListUpdated);
		this.onListUpdated();
		this.activeImportSaleTab = this.state.importSalesModal.contactGroupId-1;
	},
	destroyed(){
		this.state.openedClientsWatcher.off("list-update", this.onListUpdated);
		this.clearHideHoverCard();
	},
	components: {
		AppImportSale,
		AppImportSaleRow,
		AppPublicLink,
		AppExtraFieldsEdit,
		AppContactInfoModal,
		AppApplyDiscount,
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
